import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, TriangleAlert } from "lucide-react";
import type { LandDataGap, LandDataLevel, LandDataView } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useEvidence, type EvidenceItem } from "./evidence";
import { METRICS, metricEvidence, metricLevel, metricYear, rankOf } from "./metrics";
import { Flag, fmtNum, fmtPct } from "./shared";

export const DOMAINS: Array<{ key: string; id: string; label: string }> = [
  { key: "city", id: "sec-city", label: "城市概况" },
  { key: "districts", id: "sec-districts", label: "区县" },
  { key: "region", id: "sec-region", label: "区域研判" },
  { key: "zones", id: "sec-zones", label: "开发区" },
  { key: "land", id: "sec-land", label: "土地" },
  { key: "enterprise", id: "sec-enterprise", label: "企业" },
  { key: "roads", id: "sec-roads", label: "道路" },
  { key: "location", id: "sec-location", label: "区位" },
];

function scrollTo(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/* -------------------------------------------------------------------------- */
/* KPI strip                                                                   */
/* -------------------------------------------------------------------------- */

function Kpi({
  label,
  value,
  unit,
  sub,
  subTitle,
  level,
  valueClass,
  onClick,
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: ReactNode;
  subTitle?: string;
  level?: LandDataLevel | null;
  valueClass?: string;
  onClick?: () => void;
}): JSX.Element {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className={cn(
        "flex min-w-0 flex-col rounded-lg border border-cardline bg-white px-4 py-3 text-left",
        onClick && "transition-colors hover:border-brand-300 hover:bg-brand-50/40",
      )}
    >
      <div className="truncate text-[12px] text-zinc-500">{label}</div>
      <div className="mt-1 flex min-w-0 items-baseline gap-1">
        <span className={cn("truncate font-semibold leading-tight tabular-nums text-zinc-900", valueClass ?? "text-[22px]")}>
          {value}
        </span>
        {unit && value !== "—" ? <span className="shrink-0 text-[12px] text-zinc-500">{unit}</span> : null}
        <Flag level={level} className="ml-0.5 self-center" />
      </div>
      {sub ? (
        <div className="mt-0.5 truncate text-[11px] leading-4 text-zinc-400" title={subTitle}>
          {sub}
        </div>
      ) : null}
    </Tag>
  );
}

const CN_NUM: Record<string, number> = { 一: 1, 二: 2, 两: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9, 十: 10 };

/** "双向六车道" → "6", "双向≥8车道（卫星…）" → "≥8"; unknown → "?" */
export function laneCount(fact: string): string {
  const m = fact.match(/(≥)?\s*([0-9]+|[一二两三四五六七八九十]+)\s*车道/);
  if (!m) return "?";
  const raw = m[2] ?? "";
  const n = /^[0-9]+$/.test(raw)
    ? Number(raw)
    : raw === "十"
      ? 10
      : raw.startsWith("十")
        ? 10 + (CN_NUM[raw.slice(1)] ?? 0)
        : (CN_NUM[raw] ?? NaN);
  return Number.isNaN(n) ? "?" : `${m[1] ?? ""}${n}`;
}

const SIDE_ORDER = ["北", "东", "南", "西"];

function jumpToLandTarget(): void {
  const row = document.getElementById("land-target-row");
  if (!row) {
    scrollTo("sec-land");
    return;
  }
  row.scrollIntoView({ behavior: "smooth", block: "center" });
  row.classList.add("ring-2", "ring-brand-400");
  window.setTimeout(() => row.classList.remove("ring-2", "ring-brand-400"), 1600);
}

export function KpiStrip({ data }: { data: LandDataView }): JSX.Element {
  const evidence = useEvidence();
  const target = data.districts.find((d) => d.is_target);
  const scope = data.project.city || "区县";
  const districtName = target?.name || data.project.district || "本区";
  const ent = data.enterprise;
  const land = data.land_supply;
  const landTarget = land?.districts.find((d) => d.is_target);
  const roads = [...data.roads.roads].sort(
    (a, b) => (SIDE_ORDER.indexOf(a.side[0] ?? "") + 9) % 9 - ((SIDE_ORDER.indexOf(b.side[0] ?? "") + 9) % 9),
  );
  const roadsReview = roads.some((r) => r.level?.level === "review");

  const metricKpi = (key: "gdp" | "industrial_output" | "industrial_firms", withRank: boolean): JSX.Element => {
    const meta = METRICS[key];
    const val = target?.[key] ?? null;
    const year = target ? metricYear(target, key) : null;
    const rank = withRank ? rankOf(data.districts, key, target) : null;
    const level = target ? metricLevel(target, key) : null;
    // 区县名已在页头，这里只放年份/排名，避免挤掉关键信息
    const base = target
      ? val == null
        ? level?.note ?? ""
        : year
          ? `${year}年`
          : "年份未注明"
      : "未匹配到本项目所在区县";
    let sub: ReactNode = base;
    let subTitle = base;
    if (rank && rank.n > 1) {
      subTitle = `${districtName}：按 ${rank.n} 个 ${rank.year ?? ""} 年有数据的区县排序${
        rank.total > rank.n ? `；其余 ${rank.total - rank.n} 个区县无该年数据，未参与排名` : ""
      }`;
      sub = (
        <>
          {base} ·{" "}
          <span className="border-b border-dotted border-zinc-400">
            全市第 {rank.rank}/{rank.n}
          </span>
        </>
      );
    }
    return (
      <Kpi
        label={meta.label(target)}
        value={val == null ? "—" : fmtNum(val, meta.digits)}
        unit={meta.unit}
        sub={sub}
        subTitle={subTitle}
        level={level}
        onClick={target ? () => evidence.open(metricEvidence(target, key, scope)) : undefined}
      />
    );
  };

  const entItem = (): EvidenceItem | null => {
    if (!ent) return null;
    return {
      title: ent.provider === "qcc" ? "框选范围企业（企查查）" : `周边 ${fmtNum(ent.radius_km)}km 企业（天眼查）`,
      value: fmtNum(ent.total, 0),
      unit: "家",
      level: ent.level,
      basis: [
        ent.provider === "qcc" ? "用户框选多边形范围，含个体工商户" : "标准园区准入行业口径，不含个体工商户",
        ent.all_enterprises_capped ? "全行业主体数达平台显示上限，实际 ≥ 显示值" : "",
      ],
      source: { label: ent.source, url: ent.source_url, domain: "" },
    };
  };

  const landSub = land
    ? `${landTarget ? `本区 ${fmtNum(landTarget.parcels, 0)} 宗 · ` : ""}≤30亩 ${fmtPct(land.share_under_30mu)}`
    : "未取到";
  const landTitle = land
    ? `${land.region || "全市"} ${land.window || ""} 工业仓储成交；${landSub}；点击跳到本区所在行`
    : "";

  return (
    <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-6">
      {metricKpi("gdp", true)}
      {metricKpi("industrial_output", false)}
      {metricKpi("industrial_firms", false)}
      <Kpi
        label={ent?.provider === "qcc" ? "框选范围主体" : "可入园客户"}
        value={ent ? fmtNum(ent.total, 0) : "—"}
        unit="家"
        level={ent?.level}
        sub={ent ? (ent.provider === "qcc" ? `含个体 · 在营 ${fmtNum(ent.active_total, 0)}` : `${fmtNum(ent.radius_km)}km · 不含个体`) : "未取到"}
        onClick={ent ? () => { const it = entItem(); if (it) evidence.open(it); } : undefined}
      />
      <Kpi
        label="工业用地成交"
        value={land ? fmtNum(land.parcels, 0) : "—"}
        unit="宗"
        level={land?.level}
        sub={landSub}
        subTitle={landTitle}
        onClick={land ? jumpToLandTarget : undefined}
      />
      <button
        type="button"
        onClick={() => scrollTo("sec-roads")}
        className="flex min-w-0 flex-col rounded-lg border border-cardline bg-white px-4 py-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/40"
        title="车道数；? = 未核到，点开对照卫星图"
      >
        <div className="flex items-center gap-1 truncate text-[12px] text-zinc-500">
          四至道路车道数
          {roadsReview ? <Flag level={{ level: "review", note: "含卫星判读（下限）或多源冲突" }} /> : null}
        </div>
        {roads.length ? (
          <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
            {roads.slice(0, 4).map((r, i) => (
              <div key={`${r.road}-${i}`} className="flex items-baseline gap-1 truncate" title={`${r.side} · ${r.road}：${r.fact || "未核到"}`}>
                <span className="text-[11px] text-zinc-400">{r.side[0] ?? "·"}</span>
                <span className={cn("text-[16px] font-semibold tabular-nums", laneCount(r.fact) === "?" ? "text-zinc-300" : "text-zinc-900")}>
                  {laneCount(r.fact)}
                </span>
                {r.level?.level === "review" ? <span className="text-[10px] text-warn">▲</span> : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-1 text-[22px] font-semibold text-zinc-900">—</div>
        )}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* completeness + gaps                                                         */
/* -------------------------------------------------------------------------- */

const GAP_KIND: Record<LandDataGap["kind"], { label: string; cls: string }> = {
  pending: { label: "待补", cls: "border border-dashed border-zinc-300 text-zinc-500" },
  unpublished: { label: "查无公开", cls: "bg-zinc-100 text-zinc-500" },
  error: { label: "取数异常", cls: "bg-warn/10 text-warn" },
};

const DOMAIN_LABEL: Record<string, string> = {
  location: "区位",
  roads: "道路",
  enterprise: "企业",
  districts: "区县",
  zones: "开发区",
  land: "土地",
  other: "其他",
};

export function GapsPanel({ data }: { data: LandDataView }): JSX.Element {
  const gaps = data.gaps ?? [];
  const [showAll, setShowAll] = useState(false);
  const [openDetail, setOpenDetail] = useState<number | null>(null);
  const visible = showAll ? gaps : gaps.slice(0, 6);

  return (
    <div id="sec-gaps" className="scroll-mt-4 rounded-xl border border-cardline bg-white p-5 shadow-card">
      <div className="min-w-0">
        <div className="mb-2 flex items-center gap-1.5 text-[13px] font-medium text-zinc-700">
          <TriangleAlert className="h-3.5 w-3.5 text-warn" />
          数据缺口
          <span className="rounded-full bg-zinc-100 px-1.5 text-[11px] text-zinc-500">{gaps.length}</span>
        </div>
        {gaps.length ? (
          <ul className="divide-y divide-cardline/70 rounded-lg border border-cardline">
            {visible.map((g, i) => (
              <li key={`${g.title}-${i}`} className="px-3 py-1.5 text-[12px]">
                <div className="flex items-center gap-2">
                  <span className={cn("shrink-0 rounded px-1.5 py-px text-[10px]", GAP_KIND[g.kind]?.cls)}>
                    {GAP_KIND[g.kind]?.label ?? g.kind}
                  </span>
                  <button
                    type="button"
                    onClick={() => scrollTo(DOMAINS.find((d) => d.key === g.domain)?.id ?? "sec-gaps")}
                    className="shrink-0 text-brand-700 hover:underline"
                  >
                    {DOMAIN_LABEL[g.domain] ?? g.domain}
                  </button>
                  <span className="min-w-0 flex-1 truncate text-zinc-700" title={g.title}>
                    {g.title}
                  </span>
                  {g.detail ? (
                    <button
                      type="button"
                      onClick={() => setOpenDetail(openDetail === i ? null : i)}
                      className="shrink-0 text-zinc-400 hover:text-zinc-600"
                      aria-label="技术详情"
                    >
                      {openDetail === i ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </button>
                  ) : null}
                </div>
                {openDetail === i && g.detail ? (
                  <pre className="mt-1 whitespace-pre-wrap break-all rounded bg-canvas px-2 py-1 font-mono text-[11px] text-zinc-500">
                    {g.detail}
                  </pre>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-lg border border-cardline bg-canvas px-3 py-2 text-[12px] text-zinc-500">
            本次搜索没有记录到缺口。
          </p>
        )}
        {gaps.length > 6 ? (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="mt-1.5 text-[12px] text-brand-700 hover:underline"
          >
            {showAll ? "收起" : `展开全部 ${gaps.length} 条`}
          </button>
        ) : null}
      </div>
    </div>
  );
}
